/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable @typescript-eslint/indent */
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { CartItemDetails } from '@customTypes/cart-items';
import { store } from '@context/store';
import toggleErrorPopup from '@helpers/toggleErrorPopup';
import { find as search } from 'lodash';
import { showIsoCode } from '@helpers/currencyConverter';
import useSessionStorage from '@hooks/useSessionStorage';
import HotelDetails from '@customTypes/hotel-details';
import { useCookies } from 'react-cookie';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import useIsMobile from '@hooks/useIsMobile';
import arrowDown from '@assets/images/arrow-down-bold-white.svg';
import arrowDownBlue from '@assets/images/arrow-down-bold-dark-blue.svg';
import ReactMarkdown from 'react-markdown';
import TimeSlotSelector from '@components/ProductDetails/TimeSlotSelector';
import ProductSelector from '@components/ProductDetails/ProductSelector';
import { addToCart as addToCartEvent, noInventory } from '@events/DLP';
import TimeSlotProductSelector from '@components/ProductDetails/TimeSlotProductSelector';
import { timeSlotProductSelected } from '@events/timeSlottedProduct';
import Modal from '@components/common/Modal/Modal';
import NextAvailable from '@components/NextAvailable/NextAvailable';
import useAlgoliaEvents from '@hooks/useAlgoliaEvents';
import useAddToCart from '@hooks/useAddToCart';
import User from '@customTypes/user';
import { SELECT_PRODUCT_CARD } from '@constants/amplitudeEvents';
import { useEvents } from '@events/EventsProvider';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import Product from '@customTypes/product';
import useHotSpot from '@hooks/useHotSpot';
import useSoldOut from '@hooks/useSoldOut';
import zoomIcon from '@assets/images/zoom-icon.svg';
import ImageLoaded from '@components/common/ImageLoaded/ImageLoaded';
import ImageFallback from '@components/common/Image/ImageFallback';
import { getCdnImageUrl } from '@utils/cloudflare';
import ModalV2 from '@components/common/Modal/ModalV2';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import Button from '@solar/common/Button/ButtonV2';
import { ChevronDownIcon } from '@resortpass/solar-icons';
import cn from '@helpers/className';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import styles from './ProductDetails.module.css';
import {
  isProductUnAvailable,
  showAvailabilityWarning,
  numberOfAvailableUnits,
  getPhotoUrlFromProduct,
} from '../../helpers/product';
import ProductDetailsPrice from './ProductDetailsPrice';
import SpaBanner from './SpaBanner';

type Props = {
  products: Product[];
  product: Product;
  date: Date | undefined;
  setConfirmedDate: (date: Date | undefined) => void;
  setIsAddedToCartPopupOpen: Dispatch<SetStateAction<boolean>>;
  setShowDateWarning: Dispatch<SetStateAction<boolean>>;
  hotelId: number | undefined;
  hotelDetails: HotelDetails;
  setCartData: Dispatch<SetStateAction<CartItemDetails | undefined>>;
  cartData: CartItemDetails | undefined;
  index: number;
  showFairPricing: boolean;
};

export default function ProductDetails({
  products,
  product,
  date,
  setConfirmedDate,
  setIsAddedToCartPopupOpen,
  setShowDateWarning,
  hotelId,
  hotelDetails,
  setCartData,
  cartData,
  index,
  showFairPricing,
}: Props) {
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const [selectedTimeSlottedProduct, setSelectedTimeSlottedProduct] = useState<Product>();
  const [showLargeProductImageModal, setShowLargeProductImageModal] = useState<boolean>(false);

  const photoUrl = useMemo(() => getPhotoUrlFromProduct(product)?.url, [product]);

  const hasProductPhotoUrl = useMemo(() => {
    if (!photoUrl || photoUrl.includes('/null')) {
      return false;
    }

    return true;
  }, [photoUrl]);

  const adultCount = useMemo(() => {
    if (product.individual_billing) {
      return 1;
    }
    if (selectedTimeSlottedProduct) {
      return selectedTimeSlottedProduct.max_adults_occupancy;
    }
    return product.max_adults_occupancy;
  }, [product.individual_billing, product.max_adults_occupancy, selectedTimeSlottedProduct]);

  const childrenCount = useMemo(() => {
    if (product.individual_billing) {
      return 0;
    }

    // Time slotted + non individually billed product
    if (selectedTimeSlottedProduct) {
      if (
        selectedTimeSlottedProduct.max_person_occupancy !==
        selectedTimeSlottedProduct.max_adults_occupancy
      ) {
        return selectedTimeSlottedProduct.max_children_occupancy;
      }
      return 0;
    }

    // No time slotted + non individually billed product
    if (product.max_person_occupancy !== product.max_adults_occupancy) {
      return product.max_children_occupancy;
    }
    return 0;
  }, [
    product.individual_billing,
    product.max_adults_occupancy,
    product.max_children_occupancy,
    product.max_person_occupancy,
    selectedTimeSlottedProduct,
  ]);

  const shouldShowFairPricing = useMemo(
    () => !(date && isProductUnAvailable(product, cartData, hotelDetails, date)) && showFairPricing,
    [showFairPricing, date, product, cartData, hotelDetails],
  );

  const initialState = {
    adultCount,
    childrenCount,
    expanded: true,
  };
  const [adultCounter, setAdultCounter] = useState<number>(initialState.adultCount);
  const [childrenCounter, setChildrenCounter] = useState<number>(initialState.childrenCount);
  const [isExpanded, setIsExpanded] = useState<boolean>(initialState.expanded);
  const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);
  const [showDetailsToggle, setShowDetailsToggle] = useState<boolean>(true);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);
  const [noInventoryProducts, setNoInventoryProducts] = useState<any>([]);
  const [isNextAvailableModalOpen, setIsNextAvailableModalOpen] = useState<boolean>(false);

  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { getItem, setItem } = useSessionStorage();
  const { generateHotelPayload, generateProductPayload } = useAmplitudePayloads();
  const { track } = useEvents();
  const { getSoldOutTextForProduct } = useSoldOut();
  const [addToCart] = useAddToCart();
  const { algoliaAddedToCartEvent } = useAlgoliaEvents();
  const [cookies] = useCookies();
  const user: User = cookies.userInformation;
  const ref = useRef(null);
  const listRef = useRef<any>(null);
  const isMobile = useIsMobile();
  const { getTheTimeWithTheLowestPrice } = useHotSpot();
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);

  const isUnAvailable = !date || isProductUnAvailable(product, cartData, hotelDetails, date);
  const atLeastOneAdultWarning = 'The number of adults must be greater than 0';
  const noGuestSelectedWarning = 'Please select guests before continuing.';

  const isShowIsoCode = showIsoCode(state, getItem);

  const collapsedProducts = getItem(sessionStorageKeys.COLLAPSED_PRODUCT_CARD_DETAILS);

  const scrollIntoViewIfNeeded = (target: any) => {
    // Target is outside the viewport from the bottom
    const addToCartButton = document.getElementById(`add-to-cart-button-container-${index}`);
    const productCard =
      index === 0
        ? document.getElementById('smart-calendar-container')
        : document.getElementById(`scroll-helper-${index - 1}`);
    if (isMobile) {
      setTimeout(() => {
        productCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }
    if (
      addToCartButton &&
      target &&
      target.getBoundingClientRect().bottom > window.innerHeight - 100
    ) {
      addToCartButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleImageOpening = () => {
    setShowLargeProductImageModal(true);
  };

  // Toggle expanded state and set/delete from session storage collapsed list.
  const toggleExpanded = () => {
    const collapsedProductsList = getItem(sessionStorageKeys.COLLAPSED_PRODUCT_CARD_DETAILS);

    let newCollapsedList: number[] = [];

    // hidden to expanded
    if (!isExpanded) {
      scrollIntoViewIfNeeded(ref.current);

      // remove product id from collapsed products list.
      if (collapsedProductsList) {
        newCollapsedList = JSON.parse(collapsedProductsList).filter(
          (productId: number) => productId !== product.product_id,
        );
      }
    }

    // expanded to hidden
    if (isExpanded) {
      if (collapsedProductsList) {
        // read from session
        newCollapsedList = [...JSON.parse(collapsedProductsList), product.product_id];
      } else {
        // create a new collapsed list
        newCollapsedList = [product.product_id];
      }
    }

    // Store collapsed state
    setItem(sessionStorageKeys.COLLAPSED_PRODUCT_CARD_DETAILS, JSON.stringify(newCollapsedList));

    setIsExpanded(!isExpanded);
  };

  const scrollToDateField = () => {
    const smartCalendarElement = document.getElementById('smart-calendar-container');
    smartCalendarElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setShowDateWarning(true);
  };

  const onSelectButtonClick = () => {
    if (!date) {
      scrollToDateField();
      return;
    }

    if (product.is_product_time_group && product.children) {
      // Event
      timeSlotProductSelected(product);
    }

    const hotelPropertyGroup = generateHotelPayload(hotelDetails, products);
    const productPropertyGroup = generateProductPayload(product);

    track(SELECT_PRODUCT_CARD, {
      ...hotelPropertyGroup,
      products: [productPropertyGroup],
    });

    setIsSelectorOpen(true);
  };

  const addItemsToCart = async (productID: number, addOnItems: any) => {
    if (adultCounter === 0 && childrenCounter > 0) {
      toggleErrorPopup(atLeastOneAdultWarning, dispatch, true);
      return;
    }

    if ((adultCounter > 0 || childrenCounter > 0) && date) {
      try {
        const selectedProduct =
          product.is_product_time_group && selectedTimeSlottedProduct
            ? {
                ...product,
                ...selectedTimeSlottedProduct,
              }
            : product;

        const { updatedCartData } = await addToCart(
          addOnItems || [],
          adultCounter,
          childrenCounter,
          date,
          cartData,
          selectedProduct,
          hotelDetails,
          dispatch,
          user,
        );

        // Event
        if (product.is_product_time_group) {
          const childrenProduct = product.children?.find(
            (children) => children.product_id === productID,
          );

          if (childrenProduct) {
            addToCartEvent(
              childrenProduct,
              hotelDetails,
              addOnItems,
              adultCounter,
              childrenCounter,
              updatedCartData,
              date,
            );
          }
        } else {
          addToCartEvent(
            product,
            hotelDetails,
            addOnItems,
            adultCounter,
            childrenCounter,
            updatedCartData,
            date,
          );
        }

        if (updatedCartData) {
          setCartData(updatedCartData);
        }
        setIsAddedToCartPopupOpen(false);
        setAdultCounter(initialState.adultCount);
        setChildrenCounter(0);
        setIsSelectorOpen(false);
        setStep(0);
        if (isMobile) {
          setIsExpanded(false);
        }

        algoliaAddedToCartEvent(hotelDetails, user, sessionID);
      } catch (error) {
        toggleErrorPopup(error, dispatch);
      }
    } else if (!date) {
      scrollToDateField();
    } else {
      toggleErrorPopup(noGuestSelectedWarning, dispatch, true);
    }
  };

  const renderProductSelector = () => {
    // Single product
    if (!product.is_product_time_group) {
      return (
        <TimeSlotProductSelector
          showFairPricing={showFairPricing}
          addItemsToCart={addItemsToCart}
          adultCounter={adultCounter}
          childrenCounter={childrenCounter}
          date={date}
          hotelId={hotelId}
          hotelDetails={hotelDetails}
          product={product}
          scrollToDateField={scrollToDateField}
          setAdultCounter={setAdultCounter}
          setChildrenCounter={setChildrenCounter}
          setIsSelectorOpen={setIsSelectorOpen}
          setStep={setStep}
          cartData={cartData}
        />
      );
    }

    // Time slotted product
    switch (step) {
      case 0:
        return (
          <TimeSlotSelector
            showFairPricing={showFairPricing}
            cartData={cartData}
            date={date}
            hotel={hotelDetails}
            product={product}
            setIsOpen={setIsSelectorOpen}
            setStep={setStep}
            setSelectedTimeSlottedProduct={setSelectedTimeSlottedProduct}
          />
        );

      case 1:
        if (selectedTimeSlottedProduct) {
          return (
            <TimeSlotProductSelector
              showFairPricing={showFairPricing}
              addItemsToCart={addItemsToCart}
              adultCounter={adultCounter}
              childrenCounter={childrenCounter}
              date={date}
              hotelId={hotelId}
              hotelDetails={hotelDetails}
              product={selectedTimeSlottedProduct}
              scrollToDateField={scrollToDateField}
              setAdultCounter={setAdultCounter}
              setChildrenCounter={setChildrenCounter}
              setIsSelectorOpen={setIsSelectorOpen}
              setStep={setStep}
              isTimeSlottedProduct
              cartData={cartData}
            />
          );
        }
        return false;

      default:
        return false;
    }
  };

  const counterContainerClass = `w-full px-5 bg-rp-light-gray flex flex-col justify-center overflow-hidden transform transition-all ease-in-out duration-300 d:bg-white d:border-r-0 d:border-l-0 d:border-b-0 d:rounded-bl-xl d:p-0 ${
    isExpanded
      ? 'h-auto max-h-800 border-l border-r border-b border-solid'
      : 'h-0 max-h-0 overflow-scroll border-0 d:h-auto d:max-h-full d:overflow-hidden'
  }`;

  const selectButtonClass = `w-full h-10 rounded-xl flex justify-center items-center cursor-pointer bg-rp-primary text-white d:h-12 d:w-full d:justify-center ${
    date && isProductUnAvailable(product, cartData, hotelDetails, date)
      ? 'pointer-events-none opacity-50'
      : 'pointer-events-auto opacity-100'
  } ${
    isSelectorOpen
      ? 'd:bg-white d:text-rp-primary-dark d:border d:border-rp-primary-dark'
      : 'd:bg-rp-primary d:text-white'
  }`;
  const adultContainerSpacing = isShowIsoCode ? 'd:min-w-90 -ml-2 mr-3 d:mr-2' : 'd:min-w-78 -ml-2';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isProductInNoInventoryList = (productData: Product, selected_date: Date) =>
    !!search(noInventoryProducts, {
      product_id: productData.product_id,
      selected_date,
    });

  useEffect(() => {
    // set everything to default on date change
    if (date) {
      setAdultCounter(initialState.adultCount);
      setChildrenCounter(initialState.childrenCount);
      if (window.innerWidth > 850) {
        setIsExpanded(initialState.expanded);
      }
      if (window.innerWidth < 850) {
        if (index === 0) {
          setIsExpanded(true);
        } else {
          setIsExpanded(false);
        }
      }
    }
  }, [date, index, initialState.adultCount, initialState.childrenCount, initialState.expanded]);

  useEffect(() => {
    if (!isMobile && isExpanded && listRef && !isFirstLoad) {
      const maxHeight = 235;
      const divHeight = listRef.current?.offsetHeight;
      setShowDetailsToggle(divHeight > maxHeight);
      if (index !== 0) {
        setIsExpanded(false);
      }
      setIsFirstLoad(true);
    } else if (isMobile && window.innerWidth < 850 && !isFirstLoad) {
      if (index === 0) {
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
      }
      setIsFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, isFirstLoad]);

  useEffect(() => {
    if (!isMobile && isSelectorOpen) {
      const addToCartButton = document.getElementById(`add-to-cart-button-container-${index}`);
      const selectButton =
        index === 0
          ? document.getElementById('smart-calendar-container')
          : document.getElementById(`scroll-helper-${index - 1}`);

      if (
        addToCartButton &&
        selectButton &&
        addToCartButton.getBoundingClientRect().bottom > window.innerHeight - 100
      ) {
        selectButton?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectorOpen, isMobile]);

  // Check if the product is in the collapsed products list
  useEffect(() => {
    if (collapsedProducts) {
      const parsedProducts = JSON.parse(collapsedProducts);

      if (parsedProducts.some((productId: number) => productId === product.product_id)) {
        setIsExpanded(false);
      }
    }
  }, [product.product_id, collapsedProducts]);

  useEffect(() => {
    if (
      date &&
      product &&
      isProductUnAvailable(product, undefined, hotelDetails, date) &&
      !isProductInNoInventoryList(product, date)
    ) {
      // Event
      noInventory(product, hotelDetails, date);

      // Store product and date in state and check,
      // need this to avoid event being fired multiple times
      setNoInventoryProducts((prevState: any) => [
        ...prevState,
        {
          product_id: product.product_id,
          selected_date: date,
        },
      ]);
    }
  }, [date, product, hotelDetails, isProductInNoInventoryList]);

  return (
    <>
      <div
        className={`relative flex flex-row font-rp-pn-regular text-rp-primary-black shadow-lg rounded-b-xl border-b border-l border-rp-gray-divider rounded-t-xl min-h-235 ${styles.productDetails}`}
      >
        <div className="border border-rp-gray-divider rounded-t-xl flex flex-col d:w-3/5 d:pt-30px d:pl-6 d:pr-66px d:pb-37px d:rounded-r-none d:border-b-0 d:border-r-0 d:border-l-0">
          {/* Header with image */}
          <div className="w-full h-auto min-h-0 pb-6">
            <div className="flex flex-row items-center space-x-4">
              {hasProductPhotoUrl ? (
                <button
                  type="button"
                  onClick={handleImageOpening}
                  className="flex-shrink-0 outline-none relative"
                >
                  <div className="h-100px relative w-140px">
                    <ImageLoaded
                      url={[photoUrl, getCdnImageUrl(140, photoUrl, '', 100)]}
                      alt={product.name}
                      layout="fill"
                      classes="rounded-4"
                      priority
                    />
                  </div>

                  <div className="absolute right-2 bottom-2 flex-shrink-0 h-6 w-6 shadow-circular-icons">
                    <Image src={zoomIcon} alt="view product image" width={24} height={24} />
                  </div>
                </button>
              ) : null}

              <p className="text-19 font-rp-pn-semi-bold">{product.name}</p>
            </div>
          </div>

          {product.description ? (
            <div
              key={product.name}
              className={`flex flex-col ${styles.listContainer} ${counterContainerClass} ${
                isExpanded ? '' : styles.minimized
              }`}
              ref={listRef}
            >
              <div
                data-testid="product-description"
                className={`mb-0 pl-2 pt-0 ${showDetailsToggle ? 'pb-6' : ''}`}
              >
                <ReactMarkdown>{product.description}</ReactMarkdown>
              </div>

              {showDetailsToggle && (
                <div className="flex flex-col">
                  <SpaBanner product={product} />

                  {!isExpanded && <div className={styles.faded} />}
                  <div className="flex items-center">
                    <p
                      className="text-sm text-rp-primary-dark font-rp-pn-semi-bold cursor-pointer block"
                      onClick={toggleExpanded}
                      onKeyDown={toggleExpanded}
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </p>
                    <div
                      className={`h-3 w-3 relative ml-1.5 mb-0.5 cursor-pointer ${
                        isExpanded ? 'transform rotate-180' : 'mt-0.5'
                      }`}
                    >
                      <Image
                        src={arrowDownBlue}
                        layout="fill"
                        alt="cabana"
                        onClick={toggleExpanded}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {!product.description ? <SpaBanner product={product} /> : null}
        </div>

        <div className="w-px my-7 bg-rp-gray-divider block" />

        <div className="-ml-px border-r border-rp-gray-divider border-solid border-t flex flex-col items-start pt-7 px-7 relative rounded-r-xl w-2/5">
          {/* Availability warning when number of adult units available is less than 5 */}
          {date && showAvailabilityWarning(product, cartData) && !product.is_product_time_group && (
            <div className="w-full justify-start">
              <div className="absolute bg-rp-gold-light flex items-center justify-center rounded-xl h-5 w-20 text-11 text-rp-gold -mt-2.5 ml-3 tracking-0.3 d:ml-0">
                {`ONLY ${numberOfAvailableUnits(product, cartData)} LEFT`}
              </div>
            </div>
          )}

          {/* Unavailable Warning */}
          {date && isUnAvailable && (
            <div className="w-full justify-start">
              <div className="absolute bg-rp-light-gray-2 flex items-center justify-center rounded-xl h-5 w-24 text-11 text-rp-red uppercase -mt-2.5 ml-3 tracking-0.3 d:ml-0">
                {getSoldOutTextForProduct(product)}
              </div>
            </div>
          )}

          {/* Show rates when all the counters are 0 */}
          <div className="w-full py-5 relative flex flex-col items-center px-0 h-auto">
            <div className="flex mx-auto tracking-wide mb-5 w-full pr-3 justify-start">
              {/* When individual pricing is available (day pass etc) */}
              {product.individual_billing && (
                <>
                  {product.allow_adult && (
                    <div
                      className={`flex flex-col items-center min-w-0 whitespace-nowrap ${adultContainerSpacing}`}
                      data-testid="adult-price"
                    >
                      <p className="text-11 text-rp-gray ml-1 font-rp-pn-semi-bold">ADULT</p>

                      {product.is_product_time_group ? (
                        <p className="text-sm text-rp-gray whitespace-nowrap -mt-1 mb-1">
                          starting at
                        </p>
                      ) : null}

                      <ProductDetailsPrice
                        date={date}
                        fairPricing={showFairPricing}
                        hotelDetails={hotelDetails}
                        personType="adult"
                        product={
                          product && product.is_product_time_group && product.children
                            ? getTheTimeWithTheLowestPrice(product.children, hotelDetails, cartData)
                            : product
                        }
                      />

                      {shouldShowFairPricing ? (
                        <p className="text-12 text-rp-gray-2 whitespace-nowrap">Includes fees</p>
                      ) : null}
                    </div>
                  )}

                  {product.allow_child && (
                    <div
                      className={`flex flex-col items-center whitespace-nowrap ml-3 ${
                        isShowIsoCode ? 'min-w-78' : 'min-w-68'
                      }`}
                      data-testid="child-price"
                    >
                      <p className="text-11 text-rp-gray font-rp-pn-semi-bold">CHILD</p>
                      {product.is_product_time_group ? (
                        <p className="text-sm text-rp-gray whitespace-nowrap d:-mt-1 d:mb-1">
                          starting at
                        </p>
                      ) : null}

                      <ProductDetailsPrice
                        date={date}
                        hotelDetails={hotelDetails}
                        personType="child"
                        product={
                          product && product.is_product_time_group && product.children
                            ? getTheTimeWithTheLowestPrice(product.children, hotelDetails, cartData)
                            : product
                        }
                        fairPricing={showFairPricing}
                      />

                      {shouldShowFairPricing ? (
                        <p className="text-12 text-rp-gray-2 whitespace-nowrap">Includes fees</p>
                      ) : null}
                    </div>
                  )}

                  {product.infants_allowed && (
                    <div className="flex flex-col items-center min-w-68 mr-10 ml-3">
                      <p className="text-11 text-rp-gray font-rp-pn-semi-bold">INFANT</p>
                      <p className="text-21 font-normal font-rp-pn-regular">FREE</p>
                    </div>
                  )}
                </>
              )}

              {/* When individual pricing is off (cabana) */}
              {!product.individual_billing && product.allow_adult && (
                <div
                  className={`flex flex-col items-center align-middle d:flex-row ${
                    isShowIsoCode ? 'd:ml-0' : 'd:ml-2'
                  }`}
                  data-testid="adult-price"
                >
                  <div className="flex flex-col items-start whitespace-nowrap">
                    {product.is_product_time_group ? (
                      <div className="flex w-full">
                        <p className="text-sm text-rp-gray">starting at</p>
                      </div>
                    ) : null}

                    <ProductDetailsPrice
                      date={date}
                      hotelDetails={hotelDetails}
                      personType="adult"
                      product={
                        product && product.is_product_time_group && product.children
                          ? getTheTimeWithTheLowestPrice(product.children, hotelDetails, cartData)
                          : product
                      }
                      fairPricing={showFairPricing}
                    />

                    {shouldShowFairPricing ? (
                      <p className="text-12 text-rp-gray-2 whitespace-nowrap">Includes fees</p>
                    ) : null}
                  </div>

                  <p className="hidden text-lg text-rp-gray font-normal font-rp-pn-semi-bold mb-1.5 mx-1.5 d:block d:mb-2">
                    .
                  </p>

                  <p
                    className={`text-base font-rp-pn-semi-bold opacity-50 d:whitespace-nowrap ${
                      date ? 'd:mt-0' : 'd:mt-3px'
                    }`}
                  >
                    {`Max ${product.max_person_occupancy} People`}
                  </p>
                </div>
              )}
            </div>

            {!ctasUpdateExperiment || ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME ? (
              <button
                onClick={onSelectButtonClick}
                type="button"
                className={selectButtonClass}
                aria-label={`Select ${product.name}`}
              >
                <p className="pl-5 text-base pr-2 d:pr-0">Select</p>

                <div
                  className={`h-3 w-3 relative ml-1.5 mb-0.5 cursor-pointer block ${
                    isSelectorOpen ? 'transform rotate-180' : 'mt-0.5'
                  }`}
                >
                  <Image
                    src={isSelectorOpen ? arrowDownBlue : arrowDown}
                    layout="fill"
                    alt="cabana"
                    onClick={toggleExpanded}
                  />
                </div>
              </button>
            ) : (
              <Button
                size="lg"
                onClick={onSelectButtonClick}
                disabled={date && isProductUnAvailable(product, cartData, hotelDetails, date)}
                variant={
                  ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                    ? 'primary'
                    : 'ultramarine'
                }
              >
                Select
                <ChevronDownIcon
                  className={cn({
                    'ml-spacing-sm': true,
                    'transform rotate-180': isSelectorOpen,
                  })}
                  size={16}
                />
              </Button>
            )}
          </div>

          <div className="hidden d:block">
            <div
              className={`${
                isSelectorOpen ? 'block' : 'hidden'
              } fixed left-0 top-0 h-full w-full z-100 overflow-auto`}
              onClick={() => setIsSelectorOpen(false)}
              onKeyDown={() => setIsSelectorOpen(false)}
              role="button"
              tabIndex={0}
            />

            {isSelectorOpen && !product.is_product_time_group && (
              <ProductSelector
                showFairPricing={showFairPricing}
                addItemsToCart={addItemsToCart}
                product={product}
                cartData={cartData}
                setAdultCounter={setAdultCounter}
                setChildrenCounter={setChildrenCounter}
                hotelDetails={hotelDetails}
                isBottomDrawerOpen={false}
                index={index}
                ref={ref}
                adultCounter={adultCounter}
                childrenCounter={childrenCounter}
                date={date}
                scrollToDateField={scrollToDateField}
                setIsBottomDrawerOpen={() => {}}
              />
            )}
          </div>
        </div>
      </div>
      <div className="h-10 w-10 absolute -mt-12" id={`scroll-helper-${index}`} />

      {product.is_product_time_group && (
        <Modal
          isOpen={isSelectorOpen}
          closePopup={() => setIsSelectorOpen(false)}
          childrenClass="overflow-hidden"
          showCloseButton={false}
          hideScrollBar
        >
          {renderProductSelector()}
        </Modal>
      )}

      {/* Next Available Modal */}
      <Modal
        isOpen={isNextAvailableModalOpen}
        closePopup={() => setIsNextAvailableModalOpen(false)}
        childrenClass="pt-0 overflow-x-hidden"
        showCloseButton
        desktopWidth="d:w-auto d:max-w-856"
      >
        <NextAvailable
          hotelDetails={hotelDetails}
          product={product}
          confirmedDate={date}
          setConfirmedDate={setConfirmedDate}
          isNextAvailableModalOpen={isNextAvailableModalOpen}
          setIsNextAvailableModalOpen={setIsNextAvailableModalOpen}
        />
      </Modal>

      {/* Product Image Modal */}
      <ModalV2
        isOpen={showLargeProductImageModal}
        closePopup={() => setShowLargeProductImageModal(false)}
        childrenClass="pt-0 overflow-x-hidden"
        showCloseButton
        title={product.name}
        desktopWidth="d:w-auto d:max-w-600px"
      >
        <div className="relative" style={{ width: '600px', height: '429px' }}>
          <ImageFallback
            src={getCdnImageUrl(600, photoUrl, '', 100)}
            blurDataUrl={getCdnImageUrl(60, photoUrl, '', 10)}
            alt={`${product.name} image`}
            className="rounded-b-xl"
            loading="lazy"
            layout="fill"
          />
        </div>
      </ModalV2>
    </>
  );
}
